// Resource policy (RUNTIME-CONTRACT-V1.md §10). Layers L1 (platform profile,
// evidence-backed), L2 (commercial) and L3 (Distribution, stricter only) are
// INJECTED: every limit is optional and an absent limit is not enforced.
// This module invents no platform or commercial number; L0 engine ceilings
// live in limits.ts and are enforced by the schema regardless of policy.

export const RESOURCE_LIMIT_KEYS = [
  "layouts",
  "scenes",
  "elements",
  "assets",
  "rules",
  "actionsPerRule",
  "animationTracks",
  "keyframesTotal",
  "variables",
  "timers",
  "customEvents",
  "surveyQuestions",
  "spawnPoolMax",
  "totalBytes",
  "initialBytes",
  "imageBytes",
  "videoBytes",
  "audioBytes",
  "fontBytes",
  "videoMaxDurationMs",
  "audioMaxDurationMs",
] as const;
export type ResourceLimitKey = (typeof RESOURCE_LIMIT_KEYS)[number];
export type ResourceLimits = Readonly<Partial<Record<ResourceLimitKey, number>>>;

export interface ResourcePolicy {
  /** Stable policy identifier, e.g. "l2.rich-media" (recorded in the artifact). */
  readonly id: string;
  readonly version: number;
  readonly limits: ResourceLimits;
}

/** No limit beyond the engine's L0 ceilings. Used while L1/L2 values remain OPEN. */
export const UNRESTRICTED_POLICY: ResourcePolicy = { id: "unrestricted", version: 1, limits: {} };

/** Effective policy = intersection (minimum of every defined limit). Order-independent. */
export function combinePolicies(...policies: readonly ResourcePolicy[]): ResourcePolicy {
  const limits: Partial<Record<ResourceLimitKey, number>> = {};
  for (const key of RESOURCE_LIMIT_KEYS) {
    const values = policies.map((p) => p.limits[key]).filter((v): v is number => v !== undefined);
    if (values.length > 0) limits[key] = Math.min(...values);
  }
  const parts = [...policies].map((p) => `${p.id}@${String(p.version)}`).sort();
  return {
    id: parts.length === 1 ? (policies[0]?.id ?? "none") : parts.join("+"),
    version: parts.length === 1 ? (policies[0]?.version ?? 1) : 1,
    limits,
  };
}

/** Validates the shape of an injected policy (finite, non-negative integers; known keys only). */
export function isValidPolicy(policy: unknown): policy is ResourcePolicy {
  if (typeof policy !== "object" || policy === null) return false;
  const p = policy as Record<string, unknown>;
  if (typeof p.id !== "string" || !/^[a-z0-9][a-z0-9.+@_-]{0,127}$/.test(p.id)) return false;
  if (typeof p.version !== "number" || !Number.isInteger(p.version) || p.version < 1) return false;
  if (typeof p.limits !== "object" || p.limits === null || Array.isArray(p.limits)) return false;
  return Object.entries(p.limits).every(
    ([k, v]) =>
      (RESOURCE_LIMIT_KEYS as readonly string[]).includes(k) && typeof v === "number" && Number.isInteger(v) && v >= 0
  );
}
